import { createAction } from 'redux-actions';
import { has, includes, snakeCase, toUpper, uniq, sum } from 'lodash';
import { callApi, getEndpoint } from '../../../utils/api';
import { generateRecurringPurchase } from '../../../utils/submission/submission-utility';
import { analyticsDataLayer } from '../../utils/analytics-helper';

const EXECUTE_RECURRING_PURCHASE = 'EXECUTE_RECURRING_PURCHASE';
const UPDATE_ACKNOWLEDGEMENT = 'UPDATE_ACKNOWLEDGEMENT';
const VALIDATE_RECURRING_IRA_REDEMPTION = 'VALIDATE_RECURRING_IRA_REDEMPTION';
export const VALIDATE_RECURRING_IRA_REDEMPTION_FULFILLED = `${VALIDATE_RECURRING_IRA_REDEMPTION}_FULFILLED`;

const hasTimedOut = (payload) => {
  try {
    return (
      has(payload, 'text') &&
      includes(payload.text, '{') &&
      JSON.parse(payload.text).error === 'timeout'
    );
  } catch (e) {
    return false;
  }
};

const printErrorMessage = (payloadBody) => {
  return has(payloadBody, 'message') ? payloadBody.message : '';
};

const DEFAULT_STATE = {
  confirmationNumber: '',
  error: null,
  hasAcknowledgedIRAContributionLimit: false,
  hasFailed: false,
  isLoaded: false,
  isLoading: false,
  paperlessUpdate: null,
  transactions: [],
  tradeDate: '',
  validation: {
    validationFailed: false,
    validationMessage: {
      message: null,
    },
    validationResultLoaded: false,
    validationResultLoading: false,
    validationResults: {},
  },
};

export default function executeRecurringPurchase(
  state = DEFAULT_STATE,
  { payload, type }
) {
  switch (type) {
    case `${EXECUTE_RECURRING_PURCHASE}_PENDING`:
      return {
        ...state,
        isLoaded: false,
        isLoading: true,
        transactions: [],
      };
    case `${EXECUTE_RECURRING_PURCHASE}_FULFILLED`: {
      const someFailed = payload.body.transactions.some(
        (transaction) => !transaction.success
      );
      const allFailed = payload.body.transactions.every(
        (transaction) => !transaction.success
      );
      return {
        ...state,
        allFailed,
        confirmationNumber: null,
        hasFailed: false,
        isLoaded: true,
        isLoading: false,
        paperlessUpdate: payload.body.paperlessUpdate,
        someFailed: someFailed && !allFailed,
        tradeDate: null,
        transactions: payload.body.transactions,
      };
    }
    case `${EXECUTE_RECURRING_PURCHASE}_REJECTED`:
      return {
        ...state,
        isLoaded: true,
        isLoading: false,
        hasFailed: true,
        timeout: hasTimedOut(payload),
        message: printErrorMessage(payload.body),
        error: payload.body,
      };
    case UPDATE_ACKNOWLEDGEMENT:
      return {
        ...state,
        hasAcknowledgedIRAContributionLimit: payload,
      };
    case `${VALIDATE_RECURRING_IRA_REDEMPTION}_PENDING`:
      return {
        ...state,
        validation: {
          ...state.validation,
          validationLoading: true,
        },
      };
    case VALIDATE_RECURRING_IRA_REDEMPTION_FULFILLED:
      return {
        ...state,
        validation: {
          ...state.validation,
          validationResults: payload.body,
          validationLoading: false,
          validationResultLoaded: true,
        },
      };
    case `${VALIDATE_RECURRING_IRA_REDEMPTION}_REJECTED`:
      return {
        ...state,
        validation: {
          ...state.validation,
          validationFailed: true,
          validationMessage: payload.body,
          validationLoading: false,
          validationResults: null,
        },
      };
    default:
      return state;
  }
}

export const submitRecurringPurchase = (
  paperless,
  priorPaperless,
  purchasingFunds,
  selectedBank
) => {
  const payload = generateRecurringPurchase(
    paperless,
    priorPaperless,
    purchasingFunds,
    selectedBank
  );
  try {
    const getAnalyticsDataForTransaction = (payload) => {
      const { systematicTransactions } = payload;
      const purchaseAmounts = systematicTransactions.map((target) => {
        return target.amount;
      });

      return {
        amount: sum(purchaseAmounts),
      };
    };
    analyticsDataLayer({
      accountTypes: uniq(
        purchasingFunds.map((fund) => toUpper(snakeCase(fund.productType)))
      ),
      ...getAnalyticsDataForTransaction(payload),
      quantity: purchasingFunds.length,
    });
  } catch (e) {
    // don't let analytics errors cause other things to fail
  }
  return (dispatch) => {
    const action = createAction(EXECUTE_RECURRING_PURCHASE)(
      callApi(getEndpoint('recurringPurchase'), { method: 'POST' }, payload)
    );
    dispatch(action);
  };
};

export const validateRecurringRedemption = (validationPayloadIRA) => {
  return () => {
    const action = createAction(VALIDATE_RECURRING_IRA_REDEMPTION)(
      callApi(
        getEndpoint('validateRecurringRedemptionIRA'),
        { method: 'POST' },
        validationPayloadIRA
      )
    );
    return action;
  };
};

export const acknowledgeIRAContributionLimit = (isAcknowledged) => {
  return createAction(UPDATE_ACKNOWLEDGEMENT)(isAcknowledged);
};
